/**
 * Светильник на потолке на чертеже: люстра, спот, накладной плафон — вид сверху.
 *
 * Стоит свободно в точке плана, как мебель, но принадлежит слою электрики и
 * рисуется поверх кабелей: кабель приходит в его центр. Обозначение — белый круг
 * с цветным контуром, как у вывода под бра, — на плане весь свет читается одинаково. Мелкие споты
 * укрупняются до минимума в пикселях, люстра рисуется в натуральную величину.
 * Как и у приборов на стене, зона захвата помечена `data-role="fixture"`,
 * поэтому инструмент кабеля видит светильник как прибор, к которому можно подключиться.
 */
import type { PointerEvent } from "react";
import type { FloorItem } from "@/shared/plan";
import type { CatalogItem } from "@/shared/catalog";
import { CatalogSymbol, symbolRenderSize } from "../../CatalogSymbol";
import { ItemFrame, SELECTED_COLOR } from "../../electrical/2d/ItemFrame";

/** Габариты и цвет, если типа в каталоге больше нет — объект всё равно виден и удаляем. */
const MISSING = { width: 200, height: 200, color: "#9aa3b2" };
/** Меньше этого на экране светильник не сжимается — иначе по споту не попасть мышью. */
const MIN_PX = 14;

interface Props {
	item: FloorItem;
	/** Тип из каталога; null — тип удалён из каталога */
	info: CatalogItem | null;
	selected: boolean;
	/** Без кабеля — предупреждающее кольцо */
	unconnected?: boolean;
	/** В группе выделенного выключателя */
	highlighted?: boolean;
	mmPerPx: number;
	interactive?: boolean;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

export const CeilingLightShape = ({
	item,
	info,
	selected,
	unconnected,
	highlighted,
	mmPerPx,
	interactive = true,
	onPointerDown,
}: Props) => {
	const size = info ?? MISSING;
	const { width, height } = symbolRenderSize(size.width, size.height, mmPerPx, 1, MIN_PX);
	const px = (n: number) => n * mmPerPx;
	const stroke = selected ? SELECTED_COLOR : size.color;
	return (
		<ItemFrame
			fixtureId={item.id}
			width={width}
			height={height}
			color={size.color}
			selected={selected}
			unconnected={unconnected}
			highlighted={highlighted}
			mmPerPx={mmPerPx}
			interactive={interactive}
			onPointerDown={onPointerDown}
			backdrop={
				<ellipse
					cx={0}
					cy={0}
					rx={width / 2}
					ry={height / 2}
					fill="#ffffff"
					stroke={stroke}
					strokeWidth={px(1)}
				/>
			}
		>
			<CatalogSymbol
				item={info}
				width={width * 0.8}
				height={height * 0.8}
				color={stroke}
				mmPerPx={mmPerPx}
			/>
		</ItemFrame>
	);
};
